/**
 * Heaven Sent Promotions — Pricing Engine
 * Pure module with no React dependencies.
 * All prices calculated per the Screen Printing Price List (07/01/2026).
 */

// === SCREEN PRINT PRICE MATRIX ===
// Rows: quantity ranges, Columns: number of colors (index 0 = 1 color, index 7 = 8 colors)
// Orders below the 24-piece minimum fall through to the 24-36 row via the
// `|| SCREEN_PRINT_MATRIX[0]` fallback in lookupScreenPrintPrice, per the
// customer's rule "Orders with a quantity less than the minimum will be
// charged at the 24 piece price."
const SCREEN_PRINT_MATRIX = [
  { min: 24,   max: 36,   prices: [2.55, 3.10, 3.70, 4.15, 4.50, 4.65, 4.80, 4.95] },
  { min: 37,   max: 72,   prices: [2.20, 2.50, 2.80, 3.10, 3.45, 3.60, 3.75, 3.90] },
  { min: 73,   max: 144,  prices: [2.00, 2.22, 2.45, 2.75, 3.00, 3.15, 3.30, 3.45] },
  { min: 145,  max: 288,  prices: [1.75, 2.00, 2.16, 2.32, 2.45, 2.60, 2.75, 2.90] },
  { min: 289,  max: 500,  prices: [1.60, 1.75, 1.95, 2.05, 2.17, 2.32, 2.47, 2.62] },
  { min: 501,  max: 1000, prices: [1.36, 1.50, 1.60, 1.70, 1.85, 2.00, 2.15, 2.30] },
  { min: 1001, max: 2000, prices: [1.25, 1.33, 1.45, 1.55, 1.65, 1.80, 1.95, 2.10] },
  { min: 2001, max: 5000, prices: [1.15, 1.25, 1.32, 1.40, 1.47, 1.62, 1.77, 1.92] },
];

// === CONSTANTS ===
// Source of truth: C:\Users\keega\Desktop\Heaven_Sent_Pricing_Workbook (2).xlsx, customer-confirmed.
const MARGIN_DIVISOR = 0.58; // 42% markup
const EMBROIDERY_DECORATION_COST = 4.00;
// Setup fee is per color AND per location (e.g., 2-color front + 1-color back = 3 setups).
export const SETUP_FEE_PER_COLOR = 19.00;
const LASER_ENGRAVE_CUSTOMER_CUP = 15.00;
const LASER_ENGRAVE_WE_PROVIDE = 25.00;

// Size upcharges per garment. Customer-set values from the workbook.
// Map keys are normalized in `getSizeUpcharge` to handle both the
// "2XL" and "XXL" naming conventions SanMar uses interchangeably,
// plus ladies (L-prefixed) and tall (T-suffixed) variants.
const SIZE_UPCHARGES = {
  '2XL': 2.00,
  '3XL': 3.00,
  '4XL': 4.00,
  '5XL': 5.00,
  '6XL': 6.00,
};

function getSizeUpcharge(size) {
  if (!size) return 0;
  // Normalize: uppercase, strip ladies prefix and tall suffix so
  // "L2XL", "2XLT", "XXLT", etc. all resolve to "2XL".
  let k = String(size).trim().toUpperCase();
  if (k.startsWith('L') && k.length > 1 && /^L[2-6]?X+L/.test(k)) k = k.slice(1);
  if (k.endsWith('T')) k = k.slice(0, -1);
  // Convert legacy XXL/XXXL spellings.
  if (k === 'XXL') k = '2XL';
  if (k === 'XXXL') k = '3XL';
  return SIZE_UPCHARGES[k] || 0;
}

/**
 * Sum size upcharges for an item's size breakdown.
 * @param {Object} sizes - { S: 12, M: 4, '2XL': 2, ... }
 * @returns {number} Total upcharge in dollars (0 if no extended sizes).
 */
export function calculateSizeUpcharges(sizes) {
  if (!sizes || typeof sizes !== 'object') return 0;
  let total = 0;
  for (const [size, qty] of Object.entries(sizes)) {
    const q = Number(qty) || 0;
    if (q <= 0) continue;
    total += getSizeUpcharge(size) * q;
  }
  return Math.round(total * 100) / 100;
}

/**
 * Aggregate size-upcharge breakdown across a cart of items so the
 * summary can show each extended size on its own row (qty, per-piece,
 * subtotal). Sizes are normalized so "XXL" / "L2XL" / "2XLT" all roll
 * into the canonical "2XL" bucket.
 *
 * @param {Array<{sizes: Object}>} items - cart items
 * @returns {Array<{size: string, qty: number, perPiece: number, subtotal: number}>}
 *   sorted by canonical size order (2XL → 6XL).
 */
export function aggregateSizeUpchargeBreakdown(items) {
  if (!Array.isArray(items)) return [];
  const buckets = {};  // canonical size key -> { qty, perPiece }
  for (const item of items) {
    const sizes = item && item.sizes;
    if (!sizes || typeof sizes !== 'object') continue;
    for (const [size, qty] of Object.entries(sizes)) {
      const q = Number(qty) || 0;
      const perPiece = getSizeUpcharge(size);
      if (q <= 0 || perPiece <= 0) continue;
      // Re-normalize the size string so XXL+L2XL+2XLT all merge.
      let k = String(size).trim().toUpperCase();
      if (k.startsWith('L') && k.length > 1 && /^L[2-6]?X+L/.test(k)) k = k.slice(1);
      if (k.endsWith('T')) k = k.slice(0, -1);
      if (k === 'XXL') k = '2XL';
      if (k === 'XXXL') k = '3XL';
      if (!buckets[k]) buckets[k] = { qty: 0, perPiece };
      buckets[k].qty += q;
    }
  }
  const ORDER = ['2XL', '3XL', '4XL', '5XL', '6XL'];
  return ORDER
    .filter(k => buckets[k])
    .map(k => ({
      size: k,
      qty: buckets[k].qty,
      perPiece: buckets[k].perPiece,
      subtotal: Math.round(buckets[k].qty * buckets[k].perPiece * 100) / 100,
    }));
}

// === HELPERS ===

/**
 * Look up the per-item screen print price for a given quantity and color count.
 * @param {number} qty - Total pieces being ordered
 * @param {number} numColors - Number of colors for this print location (1-8)
 * @returns {number|null} Per-item price, or null if qty > 5000
 */
function lookupScreenPrintPrice(qty, numColors) {
  if (numColors < 1 || numColors > 8) return 0;
  if (qty > 5000) return null;

  // Below minimum: use the 24-piece price (first row)
  const row = SCREEN_PRINT_MATRIX.find(r => qty >= r.min && qty <= r.max)
    || SCREEN_PRINT_MATRIX[0]; // fallback to first row for qty < 24

  return row.prices[numColors - 1];
}

// === CORE PRICING FUNCTIONS ===

/**
 * Calculate screen print pricing.
 * @param {Object} config
 * @param {number} config.sanmarCost - Wholesale cost per item
 * @param {number} config.qty - Total pieces
 * @param {Array<{colors: number}>} config.locations - Print locations with color counts
 * @param {boolean} config.isPolyester - If true, add 1 to color lookup per location
 * @returns {Object} { perUnit, setupFee, lineTotal, contactForPricing }
 */
export function calculateScreenPrintPrice({ sanmarCost, qty, locations, isPolyester }) {
  if (qty > 5000) {
    return { perUnit: 0, setupFee: 0, lineTotal: 0, contactForPricing: true };
  }

  // Sum print costs across all locations
  let totalPrintCost = 0;
  let totalColors = 0;

  for (const loc of locations) {
    if (loc.colors <= 0) continue;
    const effectiveColors = isPolyester ? loc.colors + 1 : loc.colors;
    const clamped = Math.min(effectiveColors, 8);
    const price = lookupScreenPrintPrice(qty, clamped);
    if (price === null) {
      return { perUnit: 0, setupFee: 0, lineTotal: 0, contactForPricing: true };
    }
    totalPrintCost += price;
    totalColors += loc.colors;
  }

  const perUnit = Math.round(((sanmarCost + totalPrintCost) / MARGIN_DIVISOR) * 100) / 100;
  const setupFee = Math.round(totalColors * SETUP_FEE_PER_COLOR * 100) / 100;
  const lineTotal = Math.round((perUnit * qty + setupFee) * 100) / 100;

  return { perUnit, setupFee, lineTotal, contactForPricing: false };
}

/**
 * Calculate embroidery pricing.
 * @param {Object} config
 * @param {number} config.sanmarCost - Wholesale cost per item
 * @param {number} config.qty - Total pieces
 * @returns {Object} { perUnit, setupFee, lineTotal, contactForPricing }
 */
export function calculateEmbroideryPrice({ sanmarCost, qty }) {
  const perUnit = Math.round(((sanmarCost + EMBROIDERY_DECORATION_COST) / MARGIN_DIVISOR) * 100) / 100;
  const lineTotal = Math.round(perUnit * qty * 100) / 100;
  return { perUnit, setupFee: 0, lineTotal, contactForPricing: false };
}

/**
 * Calculate laser engraving pricing.
 * @param {Object} config
 * @param {number} config.qty - Total pieces
 * @param {boolean} config.customerProvidesCup - True if customer provides their own cup
 * @returns {Object} { perUnit, setupFee, lineTotal, contactForPricing }
 */
export function calculateLaserEngravingPrice({ qty, customerProvidesCup }) {
  const perUnit = customerProvidesCup ? LASER_ENGRAVE_CUSTOMER_CUP : LASER_ENGRAVE_WE_PROVIDE;
  const lineTotal = Math.round(perUnit * qty * 100) / 100;
  return { perUnit, setupFee: 0, lineTotal, contactForPricing: false };
}

/**
 * Calculate per-item price from logo assignments (the new logos-as-first-class
 * model). Each logo carries its own decoration type and (for SP) color count.
 * A side may carry one logo, the other side a different logo (e.g. SP front +
 * Embroidery back). This handles all combinations cleanly.
 *
 * Setup fees are computed at the cart level (per-logo, charged once regardless
 * of how many items use the logo), not here. This function returns the per-unit
 * decoration cost only — setup is the caller's job.
 *
 * @param {Object} config
 * @param {number} config.sanmarCost - Wholesale cost per item
 * @param {number} config.qty - Total pieces
 * @param {boolean} config.isPolyester - If true, add 1 to color lookup per SP location
 * @param {Object|null} config.frontLogo - Logo assigned to front side, or null
 * @param {Object|null} config.backLogo  - Logo assigned to back side, or null
 * @param {Object|null} config.leftSideLogo - Logo assigned to left side (sleeve/leg/hat side), or null
 * @param {Object|null} config.rightSideLogo - Logo assigned to right side, or null
 *   each logo: { decoration: 'Screen Print'|'Embroidery', colors: number }
 * @returns {Object} { perUnit, lineTotal, contactForPricing }
 */
export function calculateItemPriceFromLogos({ sanmarCost, qty, isPolyester, frontLogo, backLogo, leftSideLogo, rightSideLogo }) {
  if (!qty || qty <= 0) {
    return { perUnit: 0, lineTotal: 0, contactForPricing: false };
  }
  if (qty > 5000) {
    return { perUnit: 0, lineTotal: 0, contactForPricing: true };
  }

  let totalDecorationCost = 0;
  for (const logo of [frontLogo, backLogo, leftSideLogo, rightSideLogo]) {
    if (!logo) continue;
    if (logo.decoration === 'Screen Print') {
      const colors = Number(logo.colors) || 0;
      if (colors <= 0) continue;
      const effectiveColors = isPolyester ? colors + 1 : colors;
      const clamped = Math.min(effectiveColors, 8);
      const price = lookupScreenPrintPrice(qty, clamped);
      if (price === null) {
        return { perUnit: 0, lineTotal: 0, contactForPricing: true };
      }
      totalDecorationCost += price;
    } else if (logo.decoration === 'Embroidery') {
      totalDecorationCost += EMBROIDERY_DECORATION_COST;
    }
  }

  const perUnit = Math.round(((sanmarCost + totalDecorationCost) / MARGIN_DIVISOR) * 100) / 100;
  const lineTotal = Math.round(perUnit * qty * 100) / 100;
  return { perUnit, lineTotal, contactForPricing: false };
}

/**
 * Per-color, per-size pricing. The customer pays the actual SanMar
 * case rate for the variant they picked (color × size), so a white
 * S costs less than a red 2XL — SanMar runs different sale rates by
 * color and always charges more for extended sizes. The flat
 * "size upcharge" model that used to ride on top of a single
 * `product.cost` undercharged whenever the customer picked a color
 * cheaper than the catalog min, and overcharged whenever the
 * extended-size SanMar bump was smaller than our $2/$3/$4 add-on.
 *
 * Decoration cost is still computed per-piece against the total
 * order quantity so customers get the volume-discount tier they
 * actually qualify for, and the same decoration runs on every shirt
 * regardless of size.
 *
 * @param {Object} config
 * @param {Object<string, number>} config.sizeCosts - per-size SanMar
 *   wholesale rate, e.g. { S: 2.99, M: 2.99, '2XL': 4.64 }.
 *   Missing keys fall back to `config.fallbackCost` so a stale
 *   per-size table never silently produces a $0 line.
 * @param {Object<string, number>} config.sizes - per-size quantity
 *   the customer ordered, e.g. { S: 3, M: 5, '2XL': 1 }.
 * @param {number} config.fallbackCost - cost to use for sizes that
 *   don't have a per-size entry (defensive: should not happen in
 *   practice but keeps the math honest).
 * @param {boolean} config.isPolyester - +1 color tier on SP for poly.
 * @param {...Object|null} config.frontLogo, backLogo, etc.
 * @returns {Object} { perUnit, lineTotal, contactForPricing,
 *   perPieceBySize, totalQty }. `perUnit` is the qty-weighted
 *   average so the cart can keep showing one "UNIT PRICE" column
 *   without losing accuracy. `perPieceBySize` is the per-size detail
 *   for the cart breakdown rows.
 */
export function calculateItemPriceFromVariantSizes({
  sizeCosts,
  sizes,
  fallbackCost,
  isPolyester,
  frontLogo, backLogo,
  leftChestLogo, rightChestLogo,
  leftSideLogo, rightSideLogo,
}) {
  const sizeMap = sizes && typeof sizes === 'object' ? sizes : {};
  const totalQty = Object.values(sizeMap).reduce((s, q) => s + (Number(q) || 0), 0);
  if (totalQty <= 0) {
    return { perUnit: 0, lineTotal: 0, contactForPricing: false, perPieceBySize: {}, totalQty: 0 };
  }
  if (totalQty > 5000) {
    return { perUnit: 0, lineTotal: 0, contactForPricing: true, perPieceBySize: {}, totalQty };
  }

  let decorationCost = 0;
  for (const logo of [frontLogo, backLogo, leftChestLogo, rightChestLogo, leftSideLogo, rightSideLogo]) {
    if (!logo) continue;
    if (logo.decoration === 'Screen Print') {
      const colors = Number(logo.colors) || 0;
      if (colors <= 0) continue;
      const effectiveColors = isPolyester ? colors + 1 : colors;
      const clamped = Math.min(effectiveColors, 8);
      const price = lookupScreenPrintPrice(totalQty, clamped);
      if (price === null) {
        return { perUnit: 0, lineTotal: 0, contactForPricing: true, perPieceBySize: {}, totalQty };
      }
      decorationCost += price;
    } else if (logo.decoration === 'Embroidery') {
      decorationCost += EMBROIDERY_DECORATION_COST;
    }
  }

  const costTable = sizeCosts && typeof sizeCosts === 'object' ? sizeCosts : {};
  const fb = Number(fallbackCost) || 0;
  const perPieceBySize = {};
  let subtotal = 0;
  for (const [size, qty] of Object.entries(sizeMap)) {
    const q = Number(qty) || 0;
    if (q <= 0) continue;
    const sanmar = Number(costTable[size]) > 0 ? Number(costTable[size]) : fb;
    const unit = Math.round(((sanmar + decorationCost) / MARGIN_DIVISOR) * 100) / 100;
    perPieceBySize[size] = unit;
    subtotal += unit * q;
  }
  subtotal = Math.round(subtotal * 100) / 100;
  const perUnit = totalQty > 0 ? Math.round((subtotal / totalQty) * 100) / 100 : 0;

  return { perUnit, lineTotal: subtotal, contactForPricing: false, perPieceBySize, totalQty };
}

/**
 * Main pricing dispatcher.
 * @param {Object} product - Product object with cost, material, itemType
 * @param {Object} decorationConfig - { type, qty, locations?, customerProvidesCup? }
 * @returns {Object} { perUnit, setupFee, lineTotal, contactForPricing }
 */
export function calculatePrice(product, decorationConfig) {
  const { type, qty } = decorationConfig;

  if (!qty || qty <= 0) {
    return { perUnit: 0, setupFee: 0, lineTotal: 0, contactForPricing: false };
  }

  switch (type) {
    case 'Screen Print':
      return calculateScreenPrintPrice({
        sanmarCost: product.cost,
        qty,
        locations: decorationConfig.locations || [{ colors: 1 }],
        isPolyester: product.material === 'polyester',
      });

    case 'Embroidery':
      return calculateEmbroideryPrice({
        sanmarCost: product.cost,
        qty,
      });

    case 'Laser Engrave':
      return calculateLaserEngravingPrice({
        qty,
        customerProvidesCup: decorationConfig.customerProvidesCup || false,
      });

    case 'None':
    default:
      return { perUnit: 0, setupFee: 0, lineTotal: 0, contactForPricing: true };
  }
}

/**
 * Get the allowed decoration types for a product.
 * @param {Object} product - Product with allowedDecorations array
 * @returns {string[]} Array of decoration type strings
 */
export function getAllowedDecorations(product) {
  return product.allowedDecorations || ['None'];
}

/**
 * Bare-garment retail price for a product (no decoration). Useful as a
 * "starts at" floor on product cards before the customer has committed
 * to a logo / color count. Decoration adds on top once they configure
 * their design.
 * @param {Object} product - Product with `cost` (SanMar wholesale)
 * @returns {number} Per-unit retail price, rounded to cents
 */
export function calculateBasePrice(product) {
  if (!product || typeof product.cost !== 'number') return 0;
  return Math.round((product.cost / MARGIN_DIVISOR) * 100) / 100;
}

/**
 * Format a price as currency string.
 */
export function formatPrice(amount) {
  return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
